import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Card, Divider, MarkdownContainer, StatusBadge } from '../../styles/PageStyles';
import { CheckCircle, Clock, AlertCircle, RefreshCw, Loader2, X, Play, Bot, MessageCircle, Send, SendHorizonalIcon, ArrowDownCircle, ArrowUpCircle, Table } from 'lucide-react';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import GenericModal from '../common/GenericModal';
import styled from 'styled-components';
import { getNodesForPipeline } from '../../services/pipelineService';

 
const ReadMoreButton = styled.button`
  // background-color: #fff;
  // border: 1px solid #d5d9d9;
  // border-radius: 8px;
  // box-shadow: rgba(213, 217, 217, 0.5) 0 2px 5px 0;

  color: #0f1111;
  cursor: pointer;

  font-family: "Amazon Ember", sans-serif;
  font-size: 13px;
  line-height: 29px;

  padding: 0 12px;
  width: 100px;

  text-align: center;
  user-select: none;

  &:hover {
    // background-color: #f7fafa;
  }

  &:focus {
    outline: none;
    border-color: #008296;
  }
`;


const JsonViewerWrapper = styled.div`
  display: flex;
  gap: 1.5rem;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
`;

const JsonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  flex: 1; 
  min-width: 0; 
`;

const JsonSectionHeader = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
  padding-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.6rem;
  border-bottom: 2px solid rgba(0, 102, 255, 0.2);
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    flex-shrink: 0;
  }
`;

const JsonHeader = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`

const JsonContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.2rem;
  flex: 1; /* Takes remaining height */
  overflow-y: auto;
  overflow-x: auto;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.8rem;
  line-height: 1.5;
  color: #2d3748;

  /* Scrollbar styling - Vertical */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 102, 255, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 102, 255, 0.2);
    border-radius: 4px;

    &:hover {
      background: rgba(0, 102, 255, 0.3);
    }
  }


  &::-webkit-scrollbar-corner {
    background: rgba(0, 102, 255, 0.05);
  }

  white-space: pre-wrap;

  & > div {
    min-width: fit-content;
  }
`;

const CopyButton = styled.button`
  background: transparent;
  border: 1px solid rgba(0, 102, 255, 0.3);
  color: var(--text-secondary);
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
 
  &:hover {
    background: rgba(0, 102, 255, 0.05);
    border-color: rgba(0, 102, 255, 0.6);
    color: var(--text-primary);
  }
 
  &:active {
    transform: scale(0.95);
  }
 
  &.copied {
    background: rgba(76, 175, 80, 0.1);
    border-color: #4caf50;
    color: #4caf50;
 
    &::after {
      content: ' ✓';
    }
  }
`;

const JsonKey = styled.span`
  color: #0066ff; 
  font-weight: 700;

  &::after {
    content: ':';
  }
`;

const JsonString = styled.span`
  color: #000; /* Dark red for strings */
  font-weight: 600;
  &::before,
  &::after {
    content: '"';
  }
`;

const JsonNumber = styled.span`
  color: #098658; 
`;

const JsonBoolean = styled.span`
  color: #0000ff; 
`;

const JsonNull = styled.span`
  color: #6f42c1; 
`;

const JsonBracket = styled.span`
  color: #000;
  font-weight: 600;
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #a0aec0;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #cbd5e0;
`;


const formatJsonValue = (value, depth = 0) => {
  if (value === null) {
    return <JsonNull>null</JsonNull>;
  }

  if (typeof value === 'boolean') {
    return <JsonBoolean>{value.toString()}</JsonBoolean>;
  }

  if (typeof value === 'number') {
    return <JsonNumber>{value}</JsonNumber>;
  }

  if (typeof value === 'string') {
    return <JsonString>{value}</JsonString>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <>
          <JsonBracket>[</JsonBracket>
          <JsonBracket>]</JsonBracket>
        </>
      );
    }

    return (
      <>
        <JsonBracket>[</JsonBracket>
        <br />
        {value.map((item, index) => (
          <div key={index} style={{ marginLeft: `${depth + 1}rem`, minWidth: 'fit-content' }}>
            {formatJsonValue(item, depth + 1)}
            {index < value.length - 1 && ','}
            <br />
          </div>
        ))}
        <JsonBracket>]</JsonBracket>
      </>
    );
  }

  if (typeof value === 'object') {
    const keys = Object.keys(value);

    if (keys.length === 0) {
      return (
        <>
          <JsonBracket>{'{'}</JsonBracket>
          <JsonBracket>{'}'}</JsonBracket>
        </>
      );
    }

    return (
      <>
        <JsonBracket>{'{'}</JsonBracket>
        <br />
        {keys.map((key, index) => (
          <div key={key} style={{ marginLeft: `${depth + 1}rem`, minWidth: 'fit-content' }}>
            <JsonKey>{key}</JsonKey>
            {' '}
            {formatJsonValue(value[key], depth + 1)}
            {index < keys.length - 1 && ','}
            <br />
          </div>
        ))}
        <JsonBracket>{'}'}</JsonBracket>
      </>
    );
  }

  return <span>{String(value)}</span>;
};

const JsonViewer = ({ request, response, workflowVars, title = 'Data' }) => {
    const [copiedIndex, setCopiedIndex] = React.useState(null);
 
  const handleCopy = (data, index) => {
    const jsonString = JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(jsonString);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };
  const isEmpty = (obj) => {
    if (!obj) return true;
    return Object.keys(obj).length === 0 && obj.constructor === Object;
  };

  // Check which sections have data
  const hasRequest = request && !isEmpty(request);
  const hasWorkflowVars = workflowVars && !isEmpty(workflowVars);
  const hasResponse = response && !isEmpty(response);
  const hasAnyData = hasRequest || hasWorkflowVars || hasResponse;

  return (
    <JsonViewerWrapper>
      {hasAnyData ? (
        <>
          {/* REQUEST SECTION - First Column */}
          {hasRequest && (
            <JsonSection>
              <JsonSectionHeader>
                <JsonHeader>
                  <ArrowDownCircle size={20} color="#0066ff" />
                  Request
                </JsonHeader>
                <CopyButton
                  onClick={() => handleCopy(request, 0)}
                  className={copiedIndex === 0 ? 'copied' : ''}
                  title="Copy to clipboard"
                >
                  {copiedIndex === 0 ? 'Copied' : 'Copy'}
                </CopyButton>
              </JsonSectionHeader>
              <JsonContainer>
                {formatJsonValue(request)}
              </JsonContainer>
            </JsonSection>
          )}

          {/* WORKFLOW VARS SECTION - Second Column */}
          {hasWorkflowVars && (
            <JsonSection>
              <JsonSectionHeader>
                <JsonHeader>
                  <Table size={20} color="#0066ff" />
                    Workflow Vars
                </JsonHeader>
                   <CopyButton
                  onClick={() => handleCopy(workflowVars, 1)}
                  className={copiedIndex === 1 ? 'copied' : ''}
                  title="Copy to clipboard"
                >
                  {copiedIndex === 1 ? 'Copied' : 'Copy'}
                </CopyButton>
              </JsonSectionHeader>
              <JsonContainer>
                {formatJsonValue(workflowVars)}
              </JsonContainer>
            </JsonSection>
          )}

          {/* RESPONSE SECTION - Third Column */}
          {hasResponse && (
            <JsonSection>
              <JsonSectionHeader>
                <JsonHeader>
                  <ArrowUpCircle size={20} color="#0066ff" />
                  Response
                </JsonHeader>
                 <CopyButton
                  onClick={() => handleCopy(response, 2)}
                  className={copiedIndex === 2 ? 'copied' : ''}
                  title="Copy to clipboard"
                >
                  {copiedIndex === 2 ? 'Copied' : 'Copy'}
                </CopyButton>
              </JsonSectionHeader>
              <JsonContainer>
                {formatJsonValue(response)}
              </JsonContainer>
            </JsonSection>
          )}
        </>
      ) : (
        <EmptyState>
          No data available
        </EmptyState>
      )}
    </JsonViewerWrapper>
  );
};


const TimelineItem = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 0.75rem;
  position: relative;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 19px;
    top: 42px;
    bottom: -12px;
    width: 2px;
    background: #e8e8e8;
    z-index: 0;
  }
`;

const DateBadge = styled.div`
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 50%;
  background: #1e3a8a;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  z-index: 1;
  position: relative;
  font-size: 0.7rem;
`;

const NodeExecutionCard = ({ result, index, openNodeExecutonModal, loadingNodeId }) => {
  const getStatusIcon = () => {
    switch (result.transaction_status) {
      case 'CLOSED':
        return <CheckCircle size={18} className="text-green-500" />;
      case 'error':
        return <AlertCircle size={18} className="text-red-500" />;
      case 'INPROGRESS':
        return <Loader2 size={18} className="text-blue-500 animate-spin" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (result.transaction_status) {
      case 'CLOSED':
        return 'bg-green-50 border-green-200';
      case 'ERRORED':
        return 'bg-red-50 border-red-200';
      case 'INPROGRESS':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const executionDate = new Date(result.updated_at || result.created_at);
  const day = executionDate.getDate();
  const month = executionDate.toLocaleString('en-US', { month: 'short' });

  return (
    <TimelineItem>
      <DateBadge>
        <div style={{ fontSize: '0.875rem', lineHeight: 1 }}>{day}</div>
        <div style={{ fontSize: '0.625rem', lineHeight: 1, textTransform: 'uppercase' }}>{month}</div>
      </DateBadge>
      
      <div style={{ flex: 1, marginBottom: '0rem' }}>
        <div style={{
          background: '#fafbfc',
          borderRadius: '8px',
          border: '1px solid #e8eaed',
          padding: '0.75rem 1rem',
          transition: 'all 0.2s ease',
          cursor: 'default'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}>
        {/* Node Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '0.5rem',
          gap: '0.75rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flex: 1
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              minWidth: '24px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--primary-500), var(--accent-500))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 2px 8px rgba(0, 102, 255, 0.25)'
            }}>
              <Bot size={13} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--neutral-900)',
                marginBottom: '0.25rem'
              }}>{result.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{
                  fontSize: '9px',
                  color: 'var(--neutral-600)',
                  background: 'linear-gradient(135deg, #e8f3ff, #f0f7ff)',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  border: '1px solid var(--primary-200)',
                  fontWeight: '600'
                }}>{result.type}</span>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '9px',
                  color: 'var(--neutral-500)',
                  gap: '0.25rem'
                }}>
                  <Clock size={9} />
                  {formatExecutionTime(getExecutionTime(result.created_at, result.updated_at))}
                </div>
              </div>
            </div>
          </div>
          <span style={{
            fontSize: '9px',
            color: 'var(--neutral-500)',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}>{formatDateTime(result.updated_at)}</span>
        </div>
        
        {/* Message Content */}
        <div style={{
          fontSize: '0.875rem',
          lineHeight: '1.6',
          color: '#5a6c7d',
          background: '#f8f9fa',
          padding: '0.75rem',
          borderRadius: '6px',
          marginBottom: '0.5rem'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '0.25rem'
          }}>
            {result.summary ? (
              result.summary.split('\n').filter(line => line.trim()).slice(0, 4).map((line, idx) => {
                const isError = line.toLowerCase().includes('error:') || line.toLowerCase().includes('failed') || line.toLowerCase().includes('exception');
                return (
                  <div key={idx} style={{ 
                    color: isError ? '#dc3545' : '#5a6c7d',
                    fontWeight: isError ? '600' : 'normal'
                  }}>
                    {line.trim()}
                  </div>
                );
              })
            ) : (
              <div style={{ fontStyle: 'italic', color: '#9ca3af' }}>No summary available.</div>
            )}
          </div>
        </div>
        <ReadMoreButton disabled={loadingNodeId === result?.id} onClick={() => openNodeExecutonModal(result?.id)}>
          {loadingNodeId === result?.id ? 'Loading...' : 'Read More'}
        </ReadMoreButton>
        {result.entities && result.entities.length > 0 && (
          <div style={{ marginTop: '0.75rem' }}>
            <div style={{
              fontSize: '9px',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--neutral-700)',
              marginBottom: '0.5rem'
            }}>Extracted Entities</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {result.entities.map((entity, idx) => (
                <div
                  key={idx ? idx : `entity-${result.nodeId || result.id}-${idx}`}
                  style={{
                    background: 'white',
                    borderRadius: '4px',
                    padding: '0.2rem 0.4rem',
                    border: '1px solid #e0e0e0',
                    fontSize: '9px'
                  }}
                >
                  <span style={{ fontWeight: 'var(--font-medium)', color: 'var(--neutral-600)', marginRight: '0.25rem' }}>
                    {entity.type}:
                  </span>
                  <span style={{ color: 'var(--neutral-900)' }}>{entity.value}</span>
                  <span style={{ marginLeft: '0.25rem', color: 'var(--neutral-500)' }}>
                    ({Math.round(entity.confidence * 100)}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(result.inputData || result.outputData) && (
          <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e0e0e0' }}>
            {result.inputData && (
              <div style={{ marginBottom: '0.5rem' }}>
                <div style={{
                  fontSize: '9px',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--neutral-700)',
                  marginBottom: '0.25rem'
                }}>Input Data</div>
                <pre style={{
                  fontSize: '9px',
                  background: 'white',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0',
                  overflowX: 'auto',
                  margin: 0
                }}>
                  {JSON.stringify(result.inputData, null, 2)}
                </pre>
              </div>
            )}
            {result.outputData && (
              <div>
                <div style={{
                  fontSize: '9px',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--neutral-700)',
                  marginBottom: '0.25rem'
                }}>Output Data</div>
                <pre style={{
                  fontSize: '9px',
                  background: 'white',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0',
                  overflowX: 'auto',
                  margin: 0
                }}>
                  {JSON.stringify(result.outputData, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </TimelineItem>
  );
};
const getExecutionTime = (startDate, endDate) => {
  // Handle the case where dates are not defined
  if (!startDate || !endDate) {
    console.warn('Missing start or end date for execution time calculation', { startDate, endDate });
    return 0;
  }

  // Handle UTC conversion properly
  let start, end;
  
  if (typeof startDate === 'string') {
    if (startDate.endsWith('Z')) {
      start = new Date(startDate);
    } else {
      // Explicitly treat as UTC if no Z suffix
      start = new Date(startDate + 'Z');
    }
  } else {
    start = new Date(startDate);
  }
  
  if (typeof endDate === 'string') {
    if (endDate.endsWith('Z')) {
      end = new Date(endDate);
    } else {
      // Explicitly treat as UTC if no Z suffix
      end = new Date(endDate + 'Z');
    }
  } else {
    end = new Date(endDate);
  }

  // Check for valid dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    console.error('Invalid date in execution time calculation', { 
      startDate, endDate, 
      parsedStart: start, 
      parsedEnd: end 
    });
    return 0;
  }

  // Make sure end is after start
  if (end < start) {
    console.warn('End date is before start date in execution time calculation', { 
      startDate, endDate, 
      parsedStart: start.toISOString(), 
      parsedEnd: end.toISOString() 
    });
    return 0;
  }

  return end - start; // returns time in milliseconds
};

const formatExecutionTime = (milliseconds) => {
  // Handle invalid inputs gracefully
  if (milliseconds === undefined || milliseconds === null || isNaN(milliseconds)) {
    console.warn('Invalid milliseconds value passed to formatExecutionTime:', milliseconds);
    return '0ms';
  }
  
  // Ensure milliseconds is a number
  const ms = Number(milliseconds);
  
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  } else if (ms < 60000) {
    return `${(ms / 1000).toFixed(1)}s`;
  } else {
    return `${(ms / 60000).toFixed(1)}m`;
  }
};

 
const formatDateTime = (dateValue) => {
  if (!dateValue) return 'N/A';
 
  let date;
 
  // Handle UTC conversion properly
  if (dateValue instanceof Date) {
    date = new Date(dateValue);
  } else if (typeof dateValue === 'string') {
    let normalizedDate = dateValue;
    
    const microsecondsMatch = normalizedDate.match(/\.(\d{4,})/);
    if (microsecondsMatch) {
      normalizedDate = normalizedDate.replace(/\.(\d{3})\d+/, '.$1');
    }
    
    if (normalizedDate.includes('+00:00') || normalizedDate.includes('-00:00')) {
      normalizedDate = normalizedDate.replace(/[+-]00:00$/, 'Z');
    }
    
    date = new Date(normalizedDate);
  } else {
    date = new Date(dateValue);
  }
  
  if (isNaN(date.getTime())) {
    console.error('Invalid date in formatDateTime:', dateValue);
    return 'Invalid Date';
  }
 
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
 
  const timeOptions = {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  };
 
  // If it's today, just show time
  if (compareDate.getTime() === today.getTime()) {
    return date.toLocaleTimeString(undefined, timeOptions);
  }
 
  // If it's a different day, show day and month with time
  const dayOptions = {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  };
 
  return date.toLocaleString(undefined, dayOptions);
};
 
const PipelineExecutionDrawer = ({
  isOpen,
  onClose,
  onRefresh,
  onResume,
  onPaginationChange,
  pipelineStatus,
  executionResults,
  totalExecutionResults,
  isRunning,
  isLoadingMore,
  pipelineName,
  showCloseButton = false,
  onOpenChat
}) => {
  const [nodeModalOpen, setNodeModalOpen] = useState(false);
  const [currentOpenNodeDetails, setCurrentOpenNodeDetails] = useState(0);
  const [loadingNodeId, setLoadingNodeId] = useState(null);
  // Compute total execution time with thorough validation
  const totalExecutionTime = executionResults.reduce((sum, result, index) => {
    // Skip nodes without both start and end dates
    if (!result.created_at || !result.updated_at) {
      console.warn(`Node ${index} (${result.name}) missing timestamps:`, {
        created_at: result.created_at,
        updated_at: result.updated_at
      });
      return sum;
    }

    // For API-based nodes, we can have start/end dates that are exact same timestamp
    // In this case, assign a minimal execution time (100ms) for visualization purposes
    if (result.created_at === result.updated_at) {
      console.debug(`Node ${index} (${result.name}) has identical timestamps, assigning default duration`);
      return sum + 100; // Add 100ms as minimal execution time
    }
    
    const executionTime = getExecutionTime(result.created_at, result.updated_at);
    
    // Debug log for first few results to avoid console flooding
    if (index < 3) {
      console.debug(`Node ${index} execution time:`, {
        name: result.name,
        startDate: result.created_at,
        endDate: result.updated_at,
        duration: executionTime,
        durationFormatted: formatExecutionTime(executionTime)
      });
    }
    
    if (executionTime <= 0) {
      console.warn(`Node ${index} (${result.name}) has zero or negative execution time:`, {
        startDate: result.created_at,
        endDate: result.updated_at,
        calculatedTime: executionTime
      });
      return sum + 100; // Add minimal execution time for visualization
    }
    
    return sum + executionTime;
  }, 0);
  
  const successCount = executionResults.filter(r => r.transaction_status === 'CLOSED').length;
  const errorCount = executionResults.filter(r => r.transaction_status !== 'CLOSED').length;
  const scrollRef = useRef(null);
  const nodeContainerRef = useRef(null);
  // Get the current status for display
  const getCurrentStatus = () => {
    if (typeof pipelineStatus === 'string') {
      return pipelineStatus;
    } else if (pipelineStatus?.transaction_status) {
      return pipelineStatus.transaction_status;
    }
    return 'Unknown';
  };

  // Check if pipeline can be resumed
  const canResumeStatus = () => {
    const status = getCurrentStatus();
    return status && ['WAITING', 'ERRORED', 'ERROR'].includes(status.toUpperCase());
  };

  const openNodeExecutonModal = async (id) => {
    setLoadingNodeId(id);
    const localNode = executionResults.find((exeRes) => exeRes.id === id) || null;
    setCurrentOpenNodeDetails(localNode);

    try {
      const filter = { id };
      const sort = {};
      const query = '?skip=0&limit=1';
      const response = await getNodesForPipeline(filter, sort, query);
      if (Array.isArray(response) && response.length > 0) {
        setCurrentOpenNodeDetails(response[0]);
      }
      setNodeModalOpen(true);
    } catch (e) {
      console.error('Failed to fetch node execution details by id', e);
    } finally {
      setLoadingNodeId(null);
    }
  }

  // const bubbleUpPagination = useCallback((event) => {
  //   if (onPaginationChange && !isLoadingMore) {
  //     onPaginationChange(event);
  //   }
  // }, [onPaginationChange, isLoadingMore]);

  useEffect(() => {
    // Only auto-scroll to bottom on initial load or when drawer opens
    if (nodeContainerRef.current && executionResults.length > 0 && !isLoadingMore) {
      setTimeout(() => {
        const container = nodeContainerRef.current;
        // Check if container still exists before accessing scrollHeight
        if (container && executionResults.length) {
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
    }
  }, [isOpen, executionResults.length]); // i have added length dep due to length is dynamically changing.

  // Scroll event handler for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!nodeContainerRef.current || isLoadingMore) return;

      const container = nodeContainerRef.current;
      // Additional safety checks
      if (!container || container.scrollTop === undefined || !container.scrollHeight || !container.clientHeight) return;

      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Check if scrolled to bottom (within 50px threshold)
      if (scrollHeight - scrollTop - clientHeight < 50) {
        // console.log('Scrolled to bottom, requesting more data...');
        if (onPaginationChange && typeof onPaginationChange === 'function') {
          onPaginationChange('next');
        }
      }
    };

    const container = nodeContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        // Additional safety check on cleanup
        if (container && container.removeEventListener) {
          container.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [isLoadingMore, onPaginationChange]);

  // Debug effect to track pipeline status changes
  useEffect(() => {
    // console.log('Pipeline status changed:', pipelineStatus, typeof pipelineStatus);
  }, [pipelineStatus]);
  
  // Debug effect to show time conversion example when drawer opens
  useEffect(() => {
    if (isOpen && executionResults.length > 0) {
      const serverTime = executionResults[0].created_at;
      const localTime = new Date(serverTime);
      
    }
  }, [isOpen, executionResults, totalExecutionTime]);


  if (!isOpen) return null;

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white" style={{ padding: '0.75rem 1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
            <h2 style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>{pipelineName}</h2>
            <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.7)' }}>•</span>
            <span style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500' }}>{getCurrentStatus()}</span>
          </div>

          <GenericModal
            isOpen={nodeModalOpen}
            onClose={() => setNodeModalOpen(false)}
            title={'Node Execution details'}
            onConfirm={() => { setNodeModalOpen(false) }}
            cancelText="Cancel"
            confirmText="Ok"
            isLoading={false}
            size="xlg"
          >
            <JsonViewer
              request={currentOpenNodeDetails?.request}
              response={currentOpenNodeDetails?.response}
              workflowVars={currentOpenNodeDetails?.session_state?.workflow_vars || currentOpenNodeDetails?.workflow_vars}
            />
          </GenericModal>


          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {onOpenChat && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onOpenChat}
                className="text-white onBlueBtn"
                title="Open Chat"
                style={{ padding: '0.25rem', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <MessageCircle size={16} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              className="text-white onBlueBtn"
              style={{ padding: '0.25rem', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <RefreshCw size={16} />
            </Button>
            {canResumeStatus() && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onResume}
                className="text-white onBlueBtn"
                title="Resume Pipeline"
                style={{ padding: '0.25rem', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Play size={16} />
              </Button>
            )}
            {showCloseButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white onBlueBtn"
                title="Close Drawer"
                style={{ padding: '0.25rem', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={16} />
              </Button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)' }}>
          <span style={{ fontWeight: '600' }}>{totalExecutionResults} Nodes</span>
          <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>•</span>
          <span style={{ fontWeight: '600' }}>{formatExecutionTime(totalExecutionTime)}</span>
          <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>•</span>
          <span style={{ fontWeight: '600', color: '#86efac' }}>{successCount} Success</span>
        </div>
      </div>
      <div className="flex-1 min-h-0 p-r-0" style={{ padding: '1rem 1.25rem' }} ref={scrollRef}>
        <div style={{
          fontSize: '11px',
          fontWeight: '600',
          color: 'var(--neutral-600)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '0.75rem'
        }}>Execution Timeline</div>
        {isRunning && executionResults.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 size={32} className="text-blue-500 animate-spin mx-auto mb-3" />
              <p className="text-gray-600">Fetching Records...</p>
            </div>
          </div>
        )}
        {executionResults.length > 0 && (
          <div
            className="space-y-1 scorllControl"
            ref={nodeContainerRef}
          >
            {executionResults.map((result, index) => (
              <NodeExecutionCard
                key={result.nodeId || result.id || `node-${index}`}
                data-card-index={index}
                result={result}
                index={index}
                openNodeExecutonModal={openNodeExecutonModal}
                loadingNodeId={loadingNodeId}
              />
            ))}

                {/* {isLoadingMore && (
                  <div className="flex justify-center py-4 sec1">
                    <div className="text-center">
                      <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-500 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Loading more results...</p>
                          {console.log('Total execution results3:')}

                      <p className="text-xs text-gray-500">
                        {executionResults.length} of {totalExecutionResults || '?'} nodes loaded
                      </p>
                    </div>
                  </div>
                )} */}
            {isRunning && executionResults.length > 0 && (
              <div className="flex justify-center py-4 sec2">
                <div className="text-center">
                  <Loader2 size={24} className="text-blue-500 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Processing next node...</p>
                </div>
              </div>
            )}

                {/* {!isRunning && !isLoadingMore && executionResults.length > 0 && totalExecutionResults &&
                  executionResults.length >= totalExecutionResults && (
                    <div className="flex justify-center py-4 sec3">
                      <div className="text-center">
                        <div className="text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-lg">
                          {totalExecutionResults > 0 && (
                            <>
                              All {totalExecutionResults} nodes loaded
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )} */}
          </div>
        )}
      </div>
    </div>
  );
};

export default PipelineExecutionDrawer;
